/*eslint-disable camelcase*/
import { compose, map } from 'ramda';
import getConfig from '../config';
import {
    getElectronSdkVersion,
    getElectronSdkReleaseVersion,
    isCatalina,
    isVersionInIntervals,
    OS_INFO,
} from '../client-detection';
import { isApplication, OS } from '../client-detection';
import { FEATURE_KEY, FEATURES } from './declarations';
import { getElectronApi, getElectronApiName } from './desktop-api';
import { DesktopPlatform } from '../types';

const desktopToolsConfig = getConfig('DESKTOP_TOOLS') || {};
const electronFeatureSet = JSON.parse(
    String(typeof window === 'object' ? window?._electronAPI?.getFeatureSet?.() || null : null)
);

const getFeatureName = featureKey => FEATURES[featureKey].name;
const getFeatureRequiredApis = featureKey => FEATURES[featureKey].requiredApis;
// corresponding key for this feature in featureSet from DVC
const getElectronFeatureSetKey = featureKey => FEATURES[featureKey].electronFeatureSetKey;
const getFeaturePlatforms = featureKey => FEATURES[featureKey].webConfigPlatformsKey;

const getElectronApiDeps = compose(
    map((requiredApiKey: string) => ({
        requiredApiKey,
        electronApiName: getElectronApiName(requiredApiKey),
        electronApi: getElectronApi(requiredApiKey),
    })),
    getFeatureRequiredApis
);

/**
 * @param   { Object }   o - API object candidate
 * @returns { Boolean }
 */
const checkApiObjectExists = o => o !== null && o !== undefined;

/**
 * checks that feature is not disabled in electron feature set
 */
const checkFeatureIsActiveInElectronFeatureSet = featureKey => {
    const featureSetKey = getElectronFeatureSetKey(featureKey);
    if (!electronFeatureSet || !featureSetKey) return true;

    return Boolean(electronFeatureSet[featureSetKey]);
};

/**
 * checks that feature is not deactivated in app.config.js (also returns true if feature doesn't depend on config)
 */
const validateFeaturePlatform = featureKey => {
    const platformsKey = getFeaturePlatforms(featureKey) as string;
    const platforms = (desktopToolsConfig[platformsKey] as Array<DesktopPlatform>) || [
        OS.WIN,
        OS.MACOS,
    ];
    return platforms.some(platform => platform === OS_INFO);
};

const checkFeatureAPIs = electronApiDeps =>
    electronApiDeps.every(dependency => checkApiObjectExists(dependency?.electronApi));

const checkFeatureDependsOnConfigs = featureKey =>
    Boolean(getElectronFeatureSetKey(featureKey) && getFeaturePlatforms(featureKey));

const baseCalculate = res => Boolean(res.apiExist && res.featureConfigExist && res.featureSetExist);

type FeatureResult = {
    featureKey: string;
    featureName?: string;
    electronApiDeps?: unknown;
    showWarningIfApiNotProvided?: boolean;
    apiExist?: boolean;
    featureConfigExist?: boolean;
    featureSetExist?: boolean;
    calculatedFeatureFlag?: boolean;
};

/**
 * checks, should feature be used or not.
 * if feature activated in app.config and DVC's featureSet but some of feature's APIs are not provided,
 * we should log a warning about this inconsistency
 */
const validateFeature = (featureKey, calculate = baseCalculate, silent = false): FeatureResult => {
    if (!isApplication()) {
        return {
            featureKey,
        };
    }

    const res: FeatureResult = {
        featureKey,
        featureName: getFeatureName(featureKey),
        electronApiDeps: getElectronApiDeps(featureKey),
        showWarningIfApiNotProvided: !silent && checkFeatureDependsOnConfigs(featureKey),
        featureConfigExist: validateFeaturePlatform(featureKey),
        featureSetExist: checkFeatureIsActiveInElectronFeatureSet(featureKey),
    };

    res.apiExist = checkFeatureAPIs(res.electronApiDeps);
    res.calculatedFeatureFlag = calculate(res);

    return res;
};

/**
 * validate all feature flags and return single calculated result
 */
const calculateFeatureFlag = result => result.calculatedFeatureFlag ?? false;

const DVC_VERSION_REMOTE_CONTROL_SUPPORTED = '22.1.10';
const DVC_VERSION_PERSISTENT_ANNOTATIONS_SUPPORTED = '25.2.21';
const DVC_VERSION_DSDK_API_2_SUPPORTED = '25.1.21';
const DVC_VERSION_LONG_CONSTRAINTS_SUPPORTED = '23.2.31';
const DVC_VERSION_APM_KRISP_V9_SUPPORTED = '26.1.2011';
const DVC_VERSION_VP9_FLEXIBLE_MODE_SUPPORTED = '25.1.32';

export const isVersionForRemoteControlSupported = isVersionInIntervals(getElectronSdkVersion(), [
    [DVC_VERSION_REMOTE_CONTROL_SUPPORTED],
]);

const isVersionForApmKrispV9Supported = isVersionInIntervals(getElectronSdkReleaseVersion(), [
    [DVC_VERSION_APM_KRISP_V9_SUPPORTED],
]);

const isApmKrispV9Supported = (res: FeatureResult): boolean =>
    isVersionForApmKrispV9Supported && baseCalculate(res);

const getCalcWithMinDsdkVersion = (minVersion: string) => (res: FeatureResult) =>
    isVersionInIntervals(getElectronSdkVersion(), [[minVersion]]) && baseCalculate(res);

const ANALYTICS_RESULTS = validateFeature(FEATURE_KEY.ANALYTICS);
const GREEN_HALO_RESULTS = validateFeature(FEATURE_KEY.GREEN_HALO);
const PICKER_RESULTS = validateFeature(FEATURE_KEY.PICKER);
const CHECK_SS_STATUS_RESULTS = validateFeature(
    FEATURE_KEY.CHECK_SS_ACCESS_STATUS,
    res => isCatalina() && baseCalculate(res)
);
const ELECTRON_IPC_RESULTS = validateFeature(FEATURE_KEY.ELECTRON_IPC);
const SHARING_PANEL_RESULTS = validateFeature(FEATURE_KEY.SHARING_PANEL);
const FULLSCREEN_NOTIFICATIONS_RESULTS = validateFeature(FEATURE_KEY.FULLSCREEN_NOTIFICATION);
const SHARING_BAR_RESULTS = validateFeature(FEATURE_KEY.SHARING_BAR);
const SYSTEM_PREFERENCES_RESULTS = validateFeature(
    FEATURE_KEY.SYSTEM_PREFERENCES,
    res => isApplication() && res.apiExist,
    true
);
const ANNOTATION_PAINTER_RESULTS = validateFeature(FEATURE_KEY.ANNOTATIONS_PAINTER);
const ANNOTATION_TRACKER_RESULTS = validateFeature(FEATURE_KEY.ANNOTATIONS_TRACKER);
const PERSISTENT_ANNOTATIONS_RESULTS = validateFeature(
    FEATURE_KEY.PERSISTENT_ANNOTATIONS,
    getCalcWithMinDsdkVersion(DVC_VERSION_PERSISTENT_ANNOTATIONS_SUPPORTED)
);
const DSDK_API_2_RESULTS = validateFeature(
    FEATURE_KEY.DSDK_API_2,
    getCalcWithMinDsdkVersion(DVC_VERSION_DSDK_API_2_SUPPORTED)
);
const OPEN_IN_BROWSER_RESULTS = validateFeature(FEATURE_KEY.OPEN_IN_BROWSER);
const OPEN_IN_ELECTRON_RESULTS = validateFeature(FEATURE_KEY.OPEN_IN_ELECTRON);
const AVAILABLE_ML_LIBRARIES = validateFeature(FEATURE_KEY.AVAILABLE_ML_LIBRARIES);
const DESKTOP_LOG_RESULTS = validateFeature(FEATURE_KEY.DESKTOP_LOG);
const LOCALIZATION_RESULTS = validateFeature(FEATURE_KEY.LOCALIZATION);
const KRISP_AUTO_MODE_RESULTS = validateFeature(FEATURE_KEY.KRISP_AUTO_MODE);
const CUSTOM_ELECTRON_SHARING_RESULTS = validateFeature(FEATURE_KEY.CUSTOM_ELECTRON_SHARING);
const CAPTURER_ENFORCING_RESULTS = validateFeature(FEATURE_KEY.CAPTURER_ENFORCING);
const LAUNCH_DARKLY_RESULTS = validateFeature(FEATURE_KEY.LAUNCH_DARKLY);
const FEATURE_FLAGS_RESULTS = validateFeature(FEATURE_KEY.FEATURE_FLAGS);
const RC_DATA_FOR_EXTERNAL_CAPTURER = validateFeature(FEATURE_KEY.DATA_FOR_EXTERNAL_CAPTURER);
const REMOTE_CONTROL_RESULTS = validateFeature(
    FEATURE_KEY.REMOTE_CONTROL,
    getCalcWithMinDsdkVersion(DVC_VERSION_REMOTE_CONTROL_SUPPORTED)
);
const REACTIONS_RESULT = validateFeature(FEATURE_KEY.REACTIONS);
const REACTIONS_2_RESULT = validateFeature(FEATURE_KEY.REACTIONS_2);
const VIDEO_EFFECTS_RESULT = validateFeature(FEATURE_KEY.VIDEO_EFFECTS);
const VIDEO_EFFECTS_2_RESULT = validateFeature(FEATURE_KEY.VIDEO_EFFECTS_2);
const AEC_DUMP_RESULT = validateFeature(FEATURE_KEY.AEC_DUMP);
const ADVANCED_AEC_RESULT = validateFeature(FEATURE_KEY.ADVANCED_AEC);
const ADVANCED_AEC_EXACT_RESULT = validateFeature(FEATURE_KEY.ADVANCED_AEC_EXACT);
const ECHO_LEAK_DETECTION_RESULT = validateFeature(
    FEATURE_KEY.ECHO_LEAK_DETECTION,
    getCalcWithMinDsdkVersion(DVC_VERSION_LONG_CONSTRAINTS_SUPPORTED)
);
const SHARE_MEDIA_RESULT = validateFeature(
    FEATURE_KEY.SHARE_MEDIA,
    getCalcWithMinDsdkVersion(DVC_VERSION_LONG_CONSTRAINTS_SUPPORTED)
);
const SHARING_0HZ_RESULT = validateFeature(FEATURE_KEY.SHARING_0HZ);
const ECHO_LEAK_DETECTION_OPTIMIZATION_RESULT = validateFeature(
    FEATURE_KEY.ECHO_LEAK_DETECTION_OPTIMIZATION
);
const APM_KRISP_V9_RESULT = validateFeature(FEATURE_KEY.APM_KRISP_V9, isApmKrispV9Supported);
const SCREEN_SHARE_DATA_TRACK = validateFeature(FEATURE_KEY.SCREEN_SHARE_DATA_TRACK);
const SET_OPTIMIZE_FOR_MULTIPLE_MONITORS = validateFeature(
    FEATURE_KEY.SET_OPTIMIZE_FOR_MULTIPLE_MONITORS
);
const VP9_FLEXIBLE_MODE_RESULT = validateFeature(
    FEATURE_KEY.VP9_FLEXIBLE_MODE,
    getCalcWithMinDsdkVersion(DVC_VERSION_VP9_FLEXIBLE_MODE_SUPPORTED)
);

export const IS_ANALYTICS_ENABLED = calculateFeatureFlag(ANALYTICS_RESULTS);
export const IS_GREEN_HALO_ENABLED = calculateFeatureFlag(GREEN_HALO_RESULTS);
export const IS_PICKER_ENABLED = calculateFeatureFlag(PICKER_RESULTS);
export const IS_CHECK_SS_STATUS_ENABLED = calculateFeatureFlag(CHECK_SS_STATUS_RESULTS);
export const IS_ELECTRON_IPC_ENABLED = calculateFeatureFlag(ELECTRON_IPC_RESULTS);
export const IS_SHARING_PANEL_ENABLED = calculateFeatureFlag(SHARING_PANEL_RESULTS);
export const IS_FULLSCREEN_NOTIFICATIONS_ENABLED = calculateFeatureFlag(
    FULLSCREEN_NOTIFICATIONS_RESULTS
);
export const IS_SHARING_BAR_ENABLED = calculateFeatureFlag(SHARING_BAR_RESULTS);
export const IS_SYSTEM_PREFERENCES_ENABLED = calculateFeatureFlag(SYSTEM_PREFERENCES_RESULTS);

export const IS_ANNOTATION_PAINTER_ENABLED = calculateFeatureFlag(ANNOTATION_PAINTER_RESULTS);
export const IS_ANNOTATION_TRACKER_ENABLED = calculateFeatureFlag(ANNOTATION_TRACKER_RESULTS);
export const IS_PERSISTENT_ANNOTATIONS_ENABLED = calculateFeatureFlag(
    PERSISTENT_ANNOTATIONS_RESULTS
);
export const IS_DSDK_API_2_ENABLED = calculateFeatureFlag(DSDK_API_2_RESULTS);
export const IS_OPEN_IN_BROWSER_ENABLED = calculateFeatureFlag(OPEN_IN_BROWSER_RESULTS);
export const IS_OPEN_IN_ELECTRON_ENABLED = calculateFeatureFlag(OPEN_IN_ELECTRON_RESULTS);
export const IS_AVAILABLE_ML_LIBRARIES_ENABLED = calculateFeatureFlag(AVAILABLE_ML_LIBRARIES);
export const IS_DESKTOP_LOG_ENABLED = calculateFeatureFlag(DESKTOP_LOG_RESULTS);
export const IS_DESKTOP_LOCALIZATION_ENABLED = calculateFeatureFlag(LOCALIZATION_RESULTS);
export const IS_APM_KRISP_V9_SUPPORTED = calculateFeatureFlag(APM_KRISP_V9_RESULT);
export const IS_KRISP_AUTO_MODE_ENABLED =
    calculateFeatureFlag(KRISP_AUTO_MODE_RESULTS) || IS_APM_KRISP_V9_SUPPORTED;
export const IS_CUSTOM_ELECTRON_SHARING_ENABLED = calculateFeatureFlag(
    CUSTOM_ELECTRON_SHARING_RESULTS
);
export const IS_CAPTURER_ENFORCING_ENABLED = calculateFeatureFlag(CAPTURER_ENFORCING_RESULTS);
export const IS_LAUNCH_DARKLY_ENABLED = calculateFeatureFlag(LAUNCH_DARKLY_RESULTS);
export const IS_FEATURE_FLAGS_ENABLED = calculateFeatureFlag(FEATURE_FLAGS_RESULTS);
export const IS_RC_DATA_FOR_EXTERNAL_CAPTURER_ENABLED = calculateFeatureFlag(
    RC_DATA_FOR_EXTERNAL_CAPTURER
);
export const IS_REMOTE_CONTROL_ENABLED = calculateFeatureFlag(REMOTE_CONTROL_RESULTS);
export const IS_REACTIONS_ENABLED = calculateFeatureFlag(REACTIONS_RESULT);
export const IS_REACTIONS_2_ENABLED = calculateFeatureFlag(REACTIONS_2_RESULT);
export const VIDEO_EFFECTS = calculateFeatureFlag(VIDEO_EFFECTS_RESULT);
export const VIDEO_EFFECTS_2 = calculateFeatureFlag(VIDEO_EFFECTS_2_RESULT);
export const IS_VIDEO_EFFECTS_ENABLED = VIDEO_EFFECTS_2 || VIDEO_EFFECTS;
export const IS_AEC_DUMP_ENABLED = calculateFeatureFlag(AEC_DUMP_RESULT);
export const IS_ADVANCED_AEC_ENABLED = calculateFeatureFlag(ADVANCED_AEC_RESULT);
export const IS_ADVANCED_AEC_EXACT_ENABLED = calculateFeatureFlag(ADVANCED_AEC_EXACT_RESULT);
export const IS_ECHO_LEAK_DETECTION_ENABLED = calculateFeatureFlag(ECHO_LEAK_DETECTION_RESULT);
export const IS_SHARE_MEDIA_ENABLED = calculateFeatureFlag(SHARE_MEDIA_RESULT);
export const IS_SHARING_0HZ_ENABLED = calculateFeatureFlag(SHARING_0HZ_RESULT);
export const IS_ECHO_LEAK_DETECTION_OPTIMIZATION_ENABLED = calculateFeatureFlag(
    ECHO_LEAK_DETECTION_OPTIMIZATION_RESULT
);
export const IS_SCREEN_SHARE_DATA_TRACK_ENABLED = calculateFeatureFlag(SCREEN_SHARE_DATA_TRACK);
export const IS_SET_OPTIMIZE_FOR_MULTIPLE_MONITORS_ENABLED = calculateFeatureFlag(
    SET_OPTIMIZE_FOR_MULTIPLE_MONITORS
);
export const IS_VP9_FLEXIBLE_MODE_SUPPORTED = calculateFeatureFlag(VP9_FLEXIBLE_MODE_RESULT);

export const calculatedFeatures = [
    ANALYTICS_RESULTS,
    GREEN_HALO_RESULTS,
    PICKER_RESULTS,
    CHECK_SS_STATUS_RESULTS,
    ELECTRON_IPC_RESULTS,
    SHARING_PANEL_RESULTS,
    FULLSCREEN_NOTIFICATIONS_RESULTS,
    SHARING_BAR_RESULTS,
    SYSTEM_PREFERENCES_RESULTS,
    ANNOTATION_PAINTER_RESULTS,
    ANNOTATION_TRACKER_RESULTS,
    PERSISTENT_ANNOTATIONS_RESULTS,
    DSDK_API_2_RESULTS,
    OPEN_IN_BROWSER_RESULTS,
    OPEN_IN_ELECTRON_RESULTS,
    AVAILABLE_ML_LIBRARIES,
    DESKTOP_LOG_RESULTS,
    LOCALIZATION_RESULTS,
    KRISP_AUTO_MODE_RESULTS,
    CUSTOM_ELECTRON_SHARING_RESULTS,
    CAPTURER_ENFORCING_RESULTS,
    LAUNCH_DARKLY_RESULTS,
    FEATURE_FLAGS_RESULTS,
    RC_DATA_FOR_EXTERNAL_CAPTURER,
    REMOTE_CONTROL_RESULTS,
    REACTIONS_RESULT,
    REACTIONS_2_RESULT,
    VIDEO_EFFECTS_RESULT,
    VIDEO_EFFECTS_2_RESULT,
    AEC_DUMP_RESULT,
    ADVANCED_AEC_RESULT,
    ADVANCED_AEC_EXACT_RESULT,
    ECHO_LEAK_DETECTION_RESULT,
    SHARE_MEDIA_RESULT,
    SHARING_0HZ_RESULT,
    ECHO_LEAK_DETECTION_OPTIMIZATION_RESULT,
    SCREEN_SHARE_DATA_TRACK,
    SET_OPTIMIZE_FOR_MULTIPLE_MONITORS,
    VP9_FLEXIBLE_MODE_RESULT,
];
